import { toAmount } from "../../utils/maskedValue";

const monthMap = {
  Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
  Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
};

function parseDate(date) {
  if (!date) return "N/A";
  if (typeof date === "string") {
    const raw = date.trim();
    if (!raw) return "N/A";

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      return raw;
    }

    // Handle MM/DD/YYYY format
    const slashMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) {
      const [, month, day, year] = slashMatch;
      return `${year}-${month}-${day}`;
    }

    const dashDateMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dashDateMatch) {
      const [, first, second, year] = dashDateMatch;
      const firstNum = Number(first);
      const secondNum = Number(second);

      // first-second-year can be DD-MM-YYYY or MM-DD-YYYY; prefer MM-DD-YYYY
      // unless first cannot be a month.
      if (firstNum > 12 && secondNum <= 12) {
        return `${year}-${second}-${first}`;
      }

      return `${year}-${first}-${second}`;
    }

    // Handle Java Date.toString() format: "Thu Jan 01 00:00:00 IST 2026"
    const javaDateMatch = raw.match(/^\w{3}\s+(\w{3})\s+(\d{2})\s+\d{2}:\d{2}:\d{2}\s+\w+\s+(\d{4})$/);
    if (javaDateMatch) {
      const [, monAbbrev, day, year] = javaDateMatch;
      const month = monthMap[monAbbrev];
      if (month) return `${year}-${month}-${day}`;
    }

    // Already YYYY-MM-DD or ISO string
    return raw.split("T")[0];
  }
  if (typeof date === "object" && date !== null && "$date" in date) {
    const dateStr = (date).$date;
    return new Date(dateStr).toISOString().split("T")[0];
  }
  return "N/A";
}

function mapMilestoneStatus(status) {
  if (typeof status === "string") {
    const normalized = status.trim().toLowerCase();
    if (normalized === "paid") return "Paid";
    if (normalized === "submitted") return "Submitted";
  }

  return "Not Submitted";
}



function readAssignees(
  source,
  listKey,
  fallbackIdKey,
  fallbackNameKey,
) {
  const listValue = source[listKey];
  if (Array.isArray(listValue)) {
    const mapped = listValue
      .map((entry) => {
        const record = entry;
        const userId = String(record.userId ?? "").trim();
        const name = String(record.name ?? "").trim();
        if (!userId && !name) return null;
        return { userId, name };
      })
      .filter((entry) => entry !== null);

    if (mapped.length > 0) {
      return mapped;
    }
  }

  const fallbackId = String(source[fallbackIdKey] ?? "").trim();
  const fallbackName = String(source[fallbackNameKey] ?? "").trim();
  if (!fallbackId && !fallbackName) return [];
  return [{ userId: fallbackId, name: fallbackName }];
}

function mapProjectStatus(rawStatus) {
  if (typeof rawStatus === "boolean") {
    return rawStatus ? "Active" : "Inactive";
  }

  if (typeof rawStatus === "string") {
    const normalized = rawStatus.trim().toLowerCase();

    if (!normalized) return "Active";
    if (normalized === "false" || normalized === "inactive") return "Inactive";
    if (normalized === "true" || normalized === "active") return "Active";
    if (normalized === "on hold") return "On Hold";
    if (normalized === "completed") return "Completed";
    if (normalized === "at risk") return "At Risk";
  }

  return "Active";
}

function readPcrIds(source) {
  const candidates = [source.pcrId, source.pcrID, source.PCRID, source.pcrid];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const normalized = candidate
        .map((value) => String(value ?? "").trim())
        .filter((value) => value.length > 0);

      if (normalized.length > 0) {
        return normalized;
      }
    }

    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (trimmed.length > 0) {
        return [trimmed];
      }
    }
  }

  return [];
}

/** Maps the raw GET /projects/{projectId} response into the UI's IProject shape. */
export function mapProjectDetailsResponse(raw) {
  const source = raw;
  const sow = (raw.sow) || undefined;
  const cpAssignees = readAssignees(source, "cp", "cpId", "cpName");
  const dmAssignees = readAssignees(source, "dm", "dmId", "dmName");
  const pmAssignees = readAssignees(source, "pm", "pmId", "pmName");
  const pcrIds = readPcrIds(source);
  const currencyType =
    typeof sow?.currencyType === "string"
      ? sow.currencyType
      : typeof raw.currencyType === "string"
        ? raw.currencyType
        : "USD";

  const cpNames = cpAssignees.map((entry) => entry.name || entry.userId).filter(Boolean);
  const dmNames = dmAssignees.map((entry) => entry.name || entry.userId).filter(Boolean);
  const pmNames = pmAssignees.map((entry) => entry.name || entry.userId).filter(Boolean);
  const projectStatus = mapProjectStatus(raw.status);

  return {
    id: String(raw.id || raw.projectId || "N/A"),
    projectId: String(raw.projectId || raw.projectID || raw.project_id || raw.id || "N/A"),
    sowId: String(sow?.sowId || "N/A"),
    name: String(raw.projectDescription || raw.projectName || raw.name || raw.projectId || "N/A"),
    client: String(raw.accountName || raw.customerName || "N/A"),
    description: String(raw.projectDescription || raw.projectName || "N/A"),
    technology: sow?.technologyRequirements
      ? String(sow.technologyRequirements)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
      : [],
    businessArea: Array.isArray(raw.businessArea)
      ? raw.businessArea.map((item) => String(item ?? "").trim()).filter(Boolean)
      : typeof raw.businessArea === "string"
        ? raw.businessArea.split(",").map((item) => item.trim()).filter(Boolean)
        : [],
    deliveryManager: dmNames.length > 0 ? dmNames.join(", ") : String(raw.dmName || raw.deliveryManager || "N/A"),
    projectManager: pmNames.length > 0 ? pmNames.join(", ") : String(raw.pmName || raw.projectManager || "N/A"),
    startDate: parseDate(raw.projectStartDate),
    endDate: parseDate(raw.projectEndDate),
    status: projectStatus,
    cp: cpNames,
    dm: dmNames,
    pm: pmNames,
    pcrIds,
    currencyType,
    contractStatus: "NA",
    scheduleStatus: "NA",
    scopeStatus: "NA",
    scopeStatusLabel: "NA",
    redFlagged: false,
    plannedRevenue: 0,
    actualRevenue: 0,
    plannedEBITDA: 0,
    actualEBITDA: 0,
    plannedGpmPercentage: String(raw.plannedGpmPercentage ?? "0"),
    actualGpmPercentage: String(raw.actualGpmPercentage ?? "0"),
    plannedEbitdaPercentage: String(raw.plannedEbitdaPercentage ?? "0"),
    actualEbitdaPercentage: String(raw.actualEbitdaPercentage ?? "0"),
    lastReviewDate: "",
    nextReviewDate: "",
    sows: sow
      ? [
        {
          id: String(sow.sowId || "N/A"),
          sowNumber: String(sow.sowId || "N/A"),
          title: String(raw.projectDescription || raw.projectId || "N/A"),
          startDate: parseDate(sow.sowExpectedStartDate),
          endDate: parseDate(sow.sowExpectedEndDate),
          contractValue: toAmount(sow.totalContractValueInUSD),
          currencyType: String(sow.currencyType || currencyType || "USD"),
          scope: String(sow.scopeOfWork || "N/A"),
          technology: String(sow.technologyRequirements || ""),
          uploadDate: "",
          milestones: Array.isArray(sow.milestones)
            ? (sow.milestones).map((m, index) => ({
              id: String(m.milestoneId || `MS-${index + 1}`),
              name: String(m.milestoneName || "N/A"),
              dueDate: parseDate(m.startDate),
              completionDate: parseDate(m.endDate),
              paymentAmount: toAmount(m.amountInUSD),
              status: mapMilestoneStatus(m.status),
              invoiced: false,
            }))
            : [],
        },
      ]
      : [],
    invoices: Array.isArray(raw.invoiceDetails)
      ? (raw.invoiceDetails).map((invoice, index) => ({
        id: String(invoice.invoiceId || `INV-${index + 1}`),
        milestoneId: String(invoice.invoiceId || `INV-${index + 1}`),
        milestoneName: String(invoice.revenueMonth || "N/A"),
        invoiceNumber: String(invoice.invoiceId || "N/A"),
        invoiceDate: String(invoice.invoiceSubmittedDate ?? ""),
        amount: toAmount(invoice.grossInvoiceAmountInUSD),
        status: invoice.approvalStatusInFieldGlass,
        reason: (invoice.reason ) || undefined,
        dueDate: parseDate(invoice.revenueMonth),
      }))
      : [],
    risks: [],
    actionItems: [],
    reviewComments: [],
    statusReports: Array.isArray(raw.statusReports)
      ? (raw.statusReports)
      : [],
  };
}
